import { useCallback, useEffect, useState } from 'react';
import { OrganizationAddressHealthStatus } from '@mls-wallet/shared-types';
import { useMyOrganization } from '@/hooks/organization/use-my-organization';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertTriangle, X } from 'lucide-react';

function organizationAddressHealthDismissKey(orgId: string): string {
  return `den:org-address-health:dismiss:${orgId}`;
}

/**
 * Uses `useMyOrganization({ includeAddressHealth: true })` so org data + health share one SWR path.
 * Shows a dismissible warning when the API reports address env mismatch (tab session dismissal only).
 */
export function OrganizationAddressHealthBanner() {
  const { organization, addressHealth } = useMyOrganization({
    includeAddressHealth: true,
  });
  const orgId = organization?.id;
  const [dismissed, setDismissed] = useState(false);

  /**
   * Loads the dismissed status from session storage
   */
  useEffect(() => {
    // Case: no org id, emsure dismissed is false
    if (!orgId) {
      setDismissed(false);
      return;
    }
    // Case: no window, return
    if (typeof window === 'undefined') {
      return;
    }
    setDismissed(
      sessionStorage.getItem(organizationAddressHealthDismissKey(orgId)) === '1'
    );
  }, [orgId]);

  const onDismiss = useCallback(() => {
    if (!orgId || typeof window === 'undefined') {
      return;
    }
    sessionStorage.setItem(organizationAddressHealthDismissKey(orgId), '1');
    setDismissed(true);
  }, [orgId]);

  const visible =
    addressHealth?.status === OrganizationAddressHealthStatus.MISMATCH &&
    !dismissed;

  // Case: only render the banner if the org address health is a mismatch and the user has not dismissed the banner
  if (visible)
    return (
      <>
        {/* Dim the rest of the app while the banner is visible */}
        <div
          className="pointer-events-none fixed inset-0 z-40 bg-black/25"
          aria-hidden
        />
        <div className="fixed top-0 left-0 right-0 z-[60] isolate px-12 py-3 shadow-sm">
          <Alert
            variant="destructive"
            className="mx-auto max-w-none border-destructive/50 bg-white relative pr-10 text-destructive shadow-none [&>svg]:text-destructive "
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" aria-hidden />
              <AlertDescription className="text-sm">
                <b>Contracts out of date.</b> This organization was created with
                an out of date contract version. Your assets are still secure.
                Please contact the Den team for support.
              </AlertDescription>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-2 top-2 h-8 w-8 text-destructive hover:bg-red-50 hover:text-destructive"
                onClick={onDismiss}
                aria-label="Dismiss contract version warning"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </Alert>
        </div>
        {/* Flow spacer so the fixed strip does not cover the header below */}
        <div className="min-h-[5.25rem] shrink-0" aria-hidden />
      </>
    );
  // Default to returning null to avoid rendering the banner when it is not visible
  else return null;
}
